/* eslint-disable no-use-before-define */
/* eslint-disable no-underscore-dangle */
import React, { FC, useState } from 'react';
import { format } from 'date-fns';
import { useTranslate } from 'hooks/useTranslate';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { store } from 'redux/store';
import { State } from 'redux/initialState';
import Swal from 'sweetalert2';

import { setModal } from 'modules/_shared/redux/modalActions';
import { updateBeneficiary } from 'modules/beneficiaries/redux/beneficiaryActions';

import { Beneficiary } from 'types';
import { getActualSociety } from 'utils/filters';

import PauseBeneficiaryAllPlansSection from './components/PauseBeneficiaryAllPlansSection';
import PauseBeneficiaryDateSection from './components/PauseBeneficiaryDateSection';
import PauseBeneficiaryHeader from './components/PauseBeneficiaryHeader';
import PauseBeneficiaryFooter from './components/PauseBeneficiaryFooter';

import CustomLoading from '../../../_shared/components/CustomLoading';
import '../Modals.scss';

type PauseBeneficiaryModalProps = {
  beneficiaryId: string;
  beneficiary: Beneficiary;
};

const PauseBeneficiaryModal: FC<PauseBeneficiaryModalProps> = ({
  beneficiaryId,
  beneficiary,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();

  const { actualSociety } = useSelector((state: State) => state?.society);
  const user = useSelector((state: State) => state?.user);

  const [pauseStartDate, setPauseStartDate] = useState(
    format(new Date(), 'yyyy-MM-dd')
  );
  const [isIndefinitePause, setIsIndefinitePause] = useState(true);
  const [pauseEndDate, setPauseEndDate] = useState('');
  const [pauseAllPlans, setPauseAllPlans] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const handleChangePauseStartDate = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPauseStartDate(event.target.value);
  };

  const handleChangeIsIndefinitePause = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setIsIndefinitePause(event.target.checked);
    if (event.target.checked) {
      setPauseEndDate('');
    }
  };

  const handleChangePauseEndDate = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPauseEndDate(event.target.value);
  };

  const handleChangePauseAllPlans = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPauseAllPlans(event.target.checked);
  };

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  async function updateBeneficiaryData(
    id: string,
    beneficiaryData: Partial<Beneficiary>
  ) {
    try {
      await store.dispatch(
        updateBeneficiary({
          id,
          data: {
            ...beneficiaryData,
            societyId: actualSociety._id,
            userId: user._id,
          },
          showAlert: false,
        })
      );
    } catch (error) {
      console.log(error); // eslint-disable-line
    }
  }

  const handlePauseBeneficiary = async () => {
    try {
      setIsLoading(true);

      const pauseData = {
        pause: {
          startDate: pauseStartDate,
          endDate: isIndefinitePause ? null : pauseEndDate,
          isIndefinite: isIndefinitePause,
          pausedBy: user._id,
          pausedAt: new Date(),
        },
        isPaused: true,
        cascade: !!pauseAllPlans,
      };

      await updateBeneficiaryData(beneficiaryId, pauseData);
      getActualSociety(user, actualSociety._id);

      Swal.fire({
        icon: 'success',
        title: `<h4 class="nk-modal-title">${t(
          'SuccessfullyPausedBeneficiary'
        )}<br></h4>`,
        confirmButtonText: 'OK',
        allowOutsideClick: false,
        showCancelButton: false,
        confirmButtonColor: '#6576FF',
      });
    } catch (error) {
      setIsLoading(false);
    } finally {
      setIsLoading(false);
      dispatch(setModal(null));
    }
  };

  return (
    <>
      <PauseBeneficiaryHeader handleCloseModal={handleCloseModal} />

      <Modal.Body>
        <form className="form-validate is-alter">
          <PauseBeneficiaryDateSection
            pauseStartDate={pauseStartDate}
            handleChangePauseStartDate={handleChangePauseStartDate}
            isIndefinitePause={isIndefinitePause}
            handleChangeIsIndefinitePause={handleChangeIsIndefinitePause}
            pauseEndDate={pauseEndDate}
            handleChangePauseEndDate={handleChangePauseEndDate}
          />

          <PauseBeneficiaryAllPlansSection
            pauseAllPlans={pauseAllPlans}
            handleChangePauseAllPlans={handleChangePauseAllPlans}
          />
        </form>
      </Modal.Body>

      <PauseBeneficiaryFooter
        pauseStartDate={pauseStartDate}
        pauseEndDate={pauseEndDate}
        isIndefinitePause={isIndefinitePause}
        handlePauseBeneficiary={handlePauseBeneficiary}
      />
      {isLoading && <CustomLoading />}
    </>
  );
};

export default PauseBeneficiaryModal;
